if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.mediaItems = [];
        this.currentIndex = 0;
        this.touchPointers = new Set();
        this.swipeState = null;

        this.addEventListener('click', (event) => {
          if (event.target === this) this.hide();

          const button = event.target.closest('.product-media-modal__nav');
          if (!button || button.disabled) return;

          this.showMedia(
            this.currentIndex + (button.dataset.direction === 'next' ? 1 : -1),
          );
        });

        this.addEventListener('keydown', (event) => {
          if (!this.hasAttribute('open')) return;
          if (event.key === 'Tab') {
            // Recalculate after a media change or a deferred video loads.
            const focusable = getFocusableElements(this).filter((element) =>
              element.tabIndex >= 0 && !element.closest('[inert]') &&
              element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden',
            );
            if (!focusable.length) return;
            const current = focusable.indexOf(document.activeElement);
            const next = (current + (event.shiftKey ? -1 : 1) + focusable.length) % focusable.length;
            event.preventDefault();
            event.stopPropagation();
            focusable[next].focus({ preventScroll: true });
            return;
          }
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          if (event.target.closest('video, iframe, model-viewer, product-model, input, select, textarea, [contenteditable="true"]')) return;

          event.preventDefault();
          this.showMedia(
            this.currentIndex + (event.key === 'ArrowRight' ? 1 : -1),
          );
        });

        this.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'touch' || !this.hasAttribute('open')) return;

          this.touchPointers.add(event.pointerId);
          if (this.touchPointers.size !== 1) {
            if (this.swipeState) this.swipeState.pinch = true;
            return;
          }
          if (!event.target.matches('.product-media-modal__content > img.active') || (window.visualViewport?.scale || 1) > 1.01) return;

          this.swipeState = {
            startX: event.clientX,
            startY: event.clientY,
            pointerId: event.pointerId,
            verticalIntent: false,
            pinch: false,
          };
        }, { passive: true });

        this.addEventListener('pointermove', (event) => {
          if (event.pointerType !== 'touch' || !this.swipeState) return;

          const deltaX = event.clientX - this.swipeState.startX;
          const deltaY = event.clientY - this.swipeState.startY;
          if (Math.abs(deltaY) > 12 && Math.abs(deltaY) > Math.abs(deltaX)) {
            this.swipeState.verticalIntent = true;
          }
        }, { passive: true });

        const endSwipe = (event) => {
          if (event.pointerType === 'touch') this.touchPointers.delete(event.pointerId);
          if (event.pointerType !== 'touch' || !this.swipeState) return;
          if (this.touchPointers.size > 0) return;

          const swipe = this.swipeState;
          this.swipeState = null;
          if (swipe.pinch || swipe.verticalIntent || swipe.pointerId !== event.pointerId || (window.visualViewport?.scale || 1) > 1.01) return;

          const distance = event.clientX - swipe.startX;
          if (Math.abs(distance) <= Math.abs(event.clientY - swipe.startY) * 1.25) return;
          const threshold = Math.max(48, this.clientWidth * 0.15);
          if (Math.abs(distance) < threshold) return;

          this.showMedia(this.currentIndex + (distance < 0 ? 1 : -1));
        };

        this.addEventListener('pointerup', endSwipe, { passive: true });
        this.addEventListener('pointercancel', (event) => {
          this.touchPointers.delete(event.pointerId);
          this.swipeState = null;
        }, { passive: true });
      }

      show(opener) {
        this.refreshMediaItems();
        if (!this.mediaItems.length) return;
        const selectedMediaId = opener?.getAttribute('data-media-id');
        const selectedIndex = this.mediaItems.findIndex(
          (item) => item.dataset.mediaId === selectedMediaId,
        );

        this.currentIndex = selectedIndex >= 0 ? selectedIndex : 0;
        this.setActiveMedia(this.currentIndex);
        super.show(opener);
        this.querySelector('[id^="ModalClose-"]').focus({ preventScroll: true });
        requestAnimationFrame(() => {
          if (this.hasAttribute('open') && !this.contains(document.activeElement)) {
            this.querySelector('[id^="ModalClose-"]').focus({ preventScroll: true });
          }
        });
        this.loadActiveMedia();
      }

      hide() {
        this.pauseMedia();
        this.swipeState = null;
        this.touchPointers.clear();
        super.hide();
      }

      refreshMediaItems() {
        const content = this.querySelector('.product-media-modal__content');
        if (!content) {
          this.mediaItems = [];
          return;
        }

        const seenIds = new Set();
        this.mediaItems = Array.from(
          content.querySelectorAll(':scope > [data-media-id]'),
        ).filter((item) => {
          const mediaId = item.dataset.mediaId;
          if (!mediaId || seenIds.has(mediaId)) return false;
          seenIds.add(mediaId);
          return true;
        });

        this.dataset.mediaCount = this.mediaItems.length;
        const total = this.querySelector('[data-media-total]');
        if (total) total.textContent = this.mediaItems.length;
      }

      showMedia(index) {
        if (!this.mediaItems.length) return;

        const nextIndex = Math.max(
          0,
          Math.min(index, this.mediaItems.length - 1),
        );
        if (
          nextIndex === this.currentIndex &&
          this.mediaItems[nextIndex].classList.contains('active')
        ) return;

        this.pauseMedia();
        this.currentIndex = nextIndex;
        this.setActiveMedia(nextIndex);
        this.loadActiveMedia();
      }

      setActiveMedia(index) {
        const focused = document.activeElement;
        this.mediaItems.forEach((item, itemIndex) => {
          const active = itemIndex === index;
          item.classList.toggle('active', active);
          item.setAttribute('aria-hidden', String(!active));
          item.inert = !active;
        });

        const previous = this.querySelector('[data-direction="previous"]');
        const next = this.querySelector('[data-direction="next"]');
        if (previous) previous.disabled = index === 0;
        if (next) next.disabled = index >= this.mediaItems.length - 1;
        const active = this.mediaItems[index];
        if (this.hasAttribute('open') && ((focused?.disabled && this.contains(focused)) || this.mediaItems.some(item => item !== active && item.contains(focused)))) {
          this.querySelector('[id^="ModalClose-"]').focus({ preventScroll: true });
        }

        const current = this.querySelector('[data-media-current]');
        const total = this.querySelector('[data-media-total]');
        if (current) current.textContent = index + 1;
        if (total) total.textContent = this.mediaItems.length;
        this.querySelector('.product-media-modal__counter')?.setAttribute('aria-label', `Image ${index + 1} of ${this.mediaItems.length}`);
      }

      loadActiveMedia() {
        const activeMedia = this.mediaItems[this.currentIndex];
        if (!activeMedia) return;

        const deferredMedia = activeMedia.matches('deferred-media, product-model')
          ? activeMedia
          : activeMedia.querySelector('deferred-media, product-model');
        if (deferredMedia && !deferredMedia.hasAttribute('loaded') && deferredMedia.querySelector('template')?.content.querySelector('.js-youtube')) {
          deferredMedia.loadContent(false);
        }
      }

      pauseMedia() {
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      }

      showActiveMedia() {
        this.refreshMediaItems();
        const selectedMediaId = this.openedBy?.getAttribute('data-media-id');
        const selectedIndex = this.mediaItems.findIndex(
          (item) => item.dataset.mediaId === selectedMediaId,
        );
        this.currentIndex = selectedIndex >= 0 ? selectedIndex : 0;
        this.setActiveMedia(this.currentIndex);
        this.loadActiveMedia();
      }
    },
  );
}
